/**
 * client/src/lib/id.ts — the ids the *client* makes, and the one derivation it is allowed to do.
 *
 * Nothing here addresses anything in a book. A sentence is a `sid`, it is minted at ingest, and it is
 * parsed with `shared/address.ts` — that is the only coordinate system (§2.1). What lives here is the
 * device identity and a deterministic hash used for looks, neither of which is a position.
 */

/**
 * A stable per-browser device id.
 *
 * Written to `positions.deviceId` and sent as `hello.deviceId` on the room socket (§7.2). It answers
 * "is this the same phone that was reading last night", which is what lets the resume prompt say
 * *where you stopped on Tuesday* instead of guessing between two devices.
 *
 * `localStorage`, not a cookie: it must never travel on a request, least of all on an audio fetch
 * that may be served with `public, max-age=31536000, immutable` (§6.6). Note the standalone-PWA
 * caveat from §5.8 — an installed home-screen app has its own storage, so it is legitimately a
 * *different* device here, and that is the honest answer rather than a bug.
 */
const DEVICE_KEY = "ab.deviceId";

let cachedDeviceId: string | null = null;

export function deviceId(): string {
  if (cachedDeviceId) return cachedDeviceId;
  let id: string | null = null;
  try {
    id = localStorage.getItem(DEVICE_KEY);
  } catch {
    // Safari private mode throws on localStorage access. An ephemeral id is correct there: the
    // browser is telling us it will not remember this session, and pretending otherwise is worse.
    id = null;
  }
  if (!id || !id.startsWith("dev_")) {
    id = randomId("dev_", 12);
    try {
      localStorage.setItem(DEVICE_KEY, id);
    } catch {
      /* ephemeral, see above */
    }
  }
  cachedDeviceId = id;
  return id;
}

/** `prefix` + base36 from `bytes` of CSPRNG. The house id shape (`bk_`, `usr_`, `gst_`, `dev_`). */
export function randomId(prefix: string, bytes = 16): string {
  const buf = new Uint8Array(bytes);
  crypto.getRandomValues(buf);
  let out = "";
  for (const b of buf) out += b.toString(36).padStart(2, "0");
  return prefix + out.slice(0, bytes + Math.floor(bytes / 2));
}

/**
 * FNV-1a over a string, as an unsigned 32-bit int.
 *
 * Used only to pick a book's cloth colour from its id, so a book looks like the same object every
 * time you open the shelf. **Deterministic, never random** — books.karti.ai's shelf makes the same
 * point about its leaning volumes: a lean that changes on re-render is jank; a lean that is always
 * the same book is a room.
 */
export function hashId(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}
